<template>
  <Headers> </Headers>
  <div :class="[deviceType, 'progressContainer']">
    <div :class="[deviceType, 'title']">
      <h1>{{ t("ccOnBoarding.sendBack.docResubmission") }}</h1>
      <p>{{ t("ccOnBoarding.sendBack.docResubmissionDesc") }}</p>
    </div>

    <Form
      @submit="onSubmit"
      :validation-schema="schema"
      @invalid-submit="onInvalidSubmit"
    >
      <div v-if="showForm" :class="[deviceType, 'inner']">
        <div :class="[deviceType, 'content']">
          <div :class="[deviceType, 'container4']">
            <div :class="[deviceType, 'inputText']">
              <InputText
                class="inputcomponent"
                v-model="prop.sendBackDocSubmission.referenceNo"
                field="sendBackReferenceNo"
                :required="false"
                :placeholder="t('ccOnBoarding.sendBack.referenceNumber')"
                :max-length="22"
                @keydown="handleKeyDown('sendBackReferenceNo', $event)"
                :autocomplete="'new-password'"
                :custom-placeholder="sendBackPlaceholder1"
              />
            </div>
          </div>
          <div :class="[deviceType, 'container4']">
            <div :class="[deviceType, 'inputText']">
              <InputText
                ref="idNoInput"
                class="inputcomponent"
                v-model="prop.sendBackDocSubmission.idNo"
                :value="prop.sendBackDocSubmission.idNo"
                :field="'sendBackIdNo'"
                :required="false"
                :placeholder="
                  t('ccOnBoarding.basicInformation.icPassportNumber')
                "
                :max-length="12"
                :autocomplete="'new-password'"
                :pattern="computedPattern"
                :number="'false'"
                @keydown="handleKeyDown('sendBackIdNo', $event)"
                :clear="isClear"
                :type="'text'"
                :inputmode="'text'"
                :disabled="false"
                :custom-placeholder="sendBackPlaceholder2"
                @keydown.enter.prevent="handleEnterKey"
              />
            </div>
          </div>

          <div class="bottomLine">
            <div v-if="noFurtherFile">
              <div class="noFurtherFileContainer1">
                <div class="noFurtherFileSubContainer1">
                  {{ t("ccOnBoarding.sendBack.noFurtherFile") }}
                </div>
              </div>
            </div>
            <div v-if="invalidReferenceNo">
              <div class="noFurtherFileContainer2">
                <div class="noFurtherFileSubContainer2">
                  {{ t("ccOnBoarding.sendBack.invalidReferenceNo") }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showForm" :class="[deviceType, 'buttoncontainer']">
        <button
          :class="[deviceType, 'left-button']"
          type="button"
          @click="handleBack"
        >
          {{ t("ccOnBoarding.resume.buttonBack") }}
        </button>
        <button :class="[deviceType, 'right-button']" :disabled="isBtnDisabled">
          {{ t("ccOnBoarding.resume.buttonProceed") }}
        </button>
      </div>
    </Form>
    <div v-if="showRetrieve">
      <div :class="[deviceType, 'retrieveContainer']">
        <div class="text-center flex-container">
          <br />
          <div class="spinner-border" role="status">
            <span class="sr-only">{{
              t("ccOnBoarding.uploadDocument.loading2")
            }}</span>
          </div>
          <br />
          <br />
          <p class="loading">
            <b>{{ t("ccOnBoarding.sendBack.loading") }}</b>
          </p>
          <br />
          <br />
        </div>
      </div>
    </div>
    <div v-if="isSubmitError">
      <div :class="[deviceType, 'retrieveContainer']" class="error">
        <div class="retry">
          <img
            class="path-65 retry_logo"
            src="../../../../assets/icons/bio_timeout.svg"
          />
          <p>{{ t("ccOnBoarding.resume.connectionTimeout") }}</p>
        </div>
        <div :class="[deviceType, 'buttoncontainer2']">
          <!-- <div :class="[deviceType, 'left-button2']">
            {{ t("ccOnBoarding.resume.buttonStart") }}
          </div> -->
          <button :class="[deviceType, 'right-button2']" @click="onSubmit">
            {{ t("ccOnBoarding.resume.buttonRetry") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, type PropType } from "vue";
import i18n from "@/plugin/i18n";
import { inject } from "vue";
import MasterService from "@/service/restfulService/CCOnboardMasterService";
import InputText from "./../components/NewInputTextComponent.vue";
import { Form } from "vee-validate";
import Headers from "./../components/Headers.vue";
import type SendBackDocSubmission from "@/interface/SendBackDocSubmission";
import { useRouter } from "vue-router";

const deviceType = inject("deviceType");
const { t } = i18n.global;
const router = useRouter();

const showForm = ref(true);
const showRetrieve = ref(false);
const isSubmitError = ref(false);
const noFurtherFile = ref(false);
const invalidReferenceNo = ref(false);
const isClear = ref("false");
const referenceNoEligible = ref("");

const sendBackPlaceholder1 = i18n.global.t(
  "ccOnBoarding.sendBack.placeholder1"
);
const sendBackPlaceholder2 = i18n.global.t(
  "ccOnBoarding.sendBack.placeholder2"
);

const prop = defineProps({
  resumeApplication: {
    type: Boolean,
    required: true,
  },
  resumeInfo: {
    type: Object,
    required: true,
  },
  resumeDraftData: {
    type: Object,
    required: true,
  },
  recruiterId: {
    type: String,
  },
  ccFormValue: {
    type: Object,
    required: true,
  },
  verification: {
    type: String,
    required: true,
  },
  type: {
    type: String,
  },
  cardId: {
    type: String,
  },
  webFormRequestId: {
    type: String,
  },

  sendBackDocSubmission: {
    type: Object as PropType<SendBackDocSubmission>,
    required: true,
  },
});

const computedPattern = computed(() => /^\d+$/);

const handleKeyDown = (field: string, event: KeyboardEvent) => {
  const isDigit = /^[0-9]$/.test(event.key);
  const isArrowKey = /^Arrow(Left|Right)$/.test(event.key);

  const isIOSArrowKey =
    event.key === "UIKeyInputLeftArrow" || event.key === "UIKeyInputRightArrow";
  const isAndroidArrowKey =
    event.key === "ArrowLeft" || event.key === "ArrowRight";

  const isDeleteKey = event.key === "Delete";
  const isBackspace = event.key === "Backspace";
  const isTabKey = event.key === " Tab";

  const isSymbol = /[^\w\d_]/.test(event.key); // true for symbol input

  if (
    isSymbol &&
    !isBackspace &&
    !(isArrowKey || isIOSArrowKey || isAndroidArrowKey) &&
    !isDeleteKey &&
    !isTabKey
  ) {
    event.preventDefault();
  }
  noFurtherFile.value = false;
  invalidReferenceNo.value = false;

  if (field == "sendBackReferenceNo") {
    referenceNoEligible.value = "";
  }
};

const isBtnDisabled = computed(() => {
  return (
    prop.sendBackDocSubmission.referenceNo.length == 0 ||
    prop.sendBackDocSubmission.idNo.length == 0
  );
});

const onSubmit = () => {
  if (invalidReferenceNo.value == true || noFurtherFile.value == true) {
    return;
  }

  showForm.value = false;
  isSubmitError.value = false;
  showRetrieve.value = true;
  const requestModel = {
    refNo: prop.sendBackDocSubmission.referenceNo,
    idNo: prop.sendBackDocSubmission.idNo,
  };
  MasterService.sendBackDocSubmission(requestModel)
    .then((res) => {
      if (res.status == 200) {
        if (res.data.responseModel.status == "E002") {
          // application no sendback record
          noFurtherFile.value = true;
          showForm.value = true;
          showRetrieve.value = false;
        } else if (res.data.responseModel.status == "E001") {
          // reference no and id not match
          invalidReferenceNo.value = true;
          showForm.value = true;
          showRetrieve.value = false;
        } else if (res.data.responseModel.status == "FAILED") {
          // invalid reference no
          showForm.value = true;
          showRetrieve.value = false;
          referenceNoEligible.value = "false";
          invalidReference();
        } else {
          // go to sendback page
          router.push({
            path: "/webform/apply/sendBack/request",
            query: {
              webFormRequestId: res.data.responseModel.webFormRequestId,
            },
          });
        }
      }
    })
    .catch((e) => {
      showForm.value = false;
      showRetrieve.value = false;
      isSubmitError.value = true;
    });
};

const handleBack = () => {
  if (prop.type == "BIO") emit("jumpStep", 16, "", 4);
  else emit("jumpStep", 101, "", 4);
};

const schema = computed(() => {
  return {
    sendBackIdNo: "required|minLengthNumber:8|maxLengthNumber:12",
    sendBackReferenceNo:
      referenceNoEligible.value != "false"
        ? "required|fixedLength:22|referenceNo"
        : "invalidReferenceNo",
  };
});

const onInvalidSubmit = () => {};

const emit = defineEmits(["jumpStep", "newApplication"]);

const handleEnterKey = () => {};

async function invalidReference() {
  schema.value.sendBackReferenceNo = "invalidReferenceNo";
}
</script>

<style scoped>
h1 {
  color: var(--Web-Black-High, #212121);

  /* Custom 3 */
  font-family: Outfit;
  font-size: 24px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 31.2px */
}

p {
  color: var(--Web-Black-Medium, #757575);

  /* Body (Regular) */
  font-family: Outfit;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */
}

.title:is(.Mobile) {
  padding: 20px 16px 0 16px;
}

.retrieveContainer:is(.Mobile) {
  width: 90%;
  padding: 80px 0;
}

.content {
  margin-top: 40px;
  padding: 0 8%;
}

.content:is(.Mobile) {
  padding: 0;
}

.container3,
.container4 {
  display: flex;
  width: 100%;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  padding: 0;
}

.container3 {
  border-bottom: 1px solid #e4e4e4;
}

.container3:is(.Mobile) {
  max-width: none !important;
}

.container4:is(.Mobile) {
  flex-direction: column;
  gap: 0;
}

.inner:is(.Mobile) {
  padding: 0 20px;
}

.inputText {
  margin-bottom: 14px;
  width: 100%;
}

.flex_third {
  width: 30%;
}

.flex_third:is(.Mobile) {
  width: 30%;
}

.flex_inputText {
  width: 70%;
}

.flex_inputText:is(.Mobile) {
  width: 100%;
}

.buttoncontainer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding: 0 8%;
}

.buttoncontainer:is(.Mobile) {
  /* flex-wrap: wrap;
    display: flex;
    justify-content: space-between; */
  align-items: center;
  padding: 0 20px;
}

.left-button {
  width: 100%;
  height: 40px;
  padding: 8px 24px;
  border-radius: 24px;
  border: 1px solid #cb03bc;
  background-color: white;
  font-size: 14px;
  font-weight: 500;
  color: #cb03bc;
  flex: 0 0 25%;
}

.left-button {
  flex: 0 0 15%;
}

.left-button:is(.Mobile) {
  flex: 0 0 30%;
  /* display: none; */
}

.right-button {
  width: 100%;
  height: 40px;
  padding: 8px 24px;
  border-radius: 24px;
  border: none;
  background-image: var(
    --gradient-set-active,
    linear-gradient(120deg, #7653ff -4.72%, #8e4eff -3.32%, #ea27c2 45.42%)
  );
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  flex: 0 0 25%;
}

.right-button:disabled {
  width: 100%;
  height: 40px;
  padding: 8px 24px;
  border-radius: 24px;
  border: none;
  background: #dedede;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  flex: 0 0 25%;
}

.right-button:is(.Mobile) {
  flex: 0 0 68%;
}

.right-button:is(.Mobile):disabled {
  width: 100%;
  height: 40px;
  padding: 8px 24px;
  border-radius: 24px;
  border: none;
  background: #dedede;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  flex: 0 0 68%;
}

.retrieveContainer {
  display: flex;
  width: 50%;
  height: 50%;
  padding: 80px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  flex-shrink: 0;
  border-radius: 16px;
  background: #fff;
  /* box-shadow: 0px 4px 24px 0px rgba(0, 0, 0, 0.24); */
  margin: 60px auto;
}

.retrieveContainer.error {
  align-items: normal;
  padding: 40px;
}

.retrieveContainer:is(.Mobile).error {
  padding: 0;
}

.flex-container {
  margin: auto;
}

.loading {
  margin-bottom: 0;
  color: var(--Web-Black-High, #212121);
  text-align: center;

  /* H2 */
  font-family: Outfit;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 26px */
}

.spinner-border {
  width: 5rem;
  height: 5rem;
}

.retry p {
  color: var(--Web-Black-High, #212121);
  text-align: center;

  /* H2 */
  font-family: Outfit;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 26px */
}

.retry_logo {
  width: 160px;
  height: 160px;
  display: flex;
  margin: auto;
}

@media (max-width: 1170px) {
  .retrieveContainer {
    width: 80%;
  }
}

@media (max-width: 700px) {
  .container4:is(.Desktop, .Tablet) {
    flex-direction: column;
    gap: 0;
  }

  .container3:is(.Desktop, .Tablet) {
    flex-direction: column;
    gap: 14px;
  }

  .flex_third:is(.Desktop, .Tablet) {
    width: 100%;
  }

  .flex_dropdown:is(.Desktop, .Tablet),
  .flex_inputText:is(.Desktop, .Tablet) {
    width: 100%;
  }

  .buttoncontainer:is(.Desktop, .Tablet) {
    gap: 10px;
  }

  .left-button:is(.Desktop, .Tablet) {
    flex: 0 0 30%;
    /* display: none; */
  }

  .right-button:is(.Desktop, .Tablet) {
    flex: 0 0 68%;
  }

  .right-button:disabled:is(.Desktop, .Tablet) {
    flex: 0 0 68%;
  }

  .retrieveContainer {
    width: 100%;
  }
}

.buttoncontainer2 {
  display: flex;
  align-items: center;
  margin-top: 24px;
}

.buttoncontainer2:is(.Desktop, .Tablet) {
  gap: 16px;
}

.buttoncontainer2:is(.Mobile) {
  /* flex-wrap: wrap;
    display: flex;
    justify-content: space-between; */
  align-items: center;
  padding: 0;
}

.left-button2 {
  width: 100%;
  height: 40px;
  padding: 8px 24px;
  border-radius: 24px;
  border: 1px solid #cb03bc;
  background-color: white;
  font-size: 14px;
  font-weight: 500;
  color: #cb03bc;
  flex: 1;
  text-align: center;
}

.right-button2 {
  width: 100%;
  max-width: 400px;
  margin: auto;
  height: 40px;
  padding: 8px 24px;
  border-radius: 24px;
  border: none;
  background-image: var(
    --gradient-set-active,
    linear-gradient(120deg, #7653ff -4.72%, #8e4eff -3.32%, #ea27c2 45.42%)
  );
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  flex: 1;
}

@media (max-width: 820px) {
  .buttoncontainer2 {
    display: block;
  }

  .left-button2 {
    margin-bottom: 16px;
  }
}

.flex_dropdown {
  margin-bottom: 14px;
  display: flex;
  width: 30%;
  box-sizing: border-box;
  position: relative;
  display: inline-block;
}

.flex_inputText {
  margin-bottom: 14px;
  width: 70%;
}

.flex_dropdown:is(.Mobile),
.flex_inputText:is(.Mobile) {
  width: 100%;
}

.bottomLine {
  border-bottom: 1px solid #e4e4e4;
}

.noFurtherFileContainer1 {
  width: -webkit-fill-available;
  padding: 12px 20px;
  background: #eaf7fe;
  border-radius: 8px;
  overflow: hidden;
  align-items: flex-start;
  gap: 8px;
  display: inline-flex;
  border-left: 6px solid rgb(122, 196, 236);
  margin-bottom: 44px;
}

.noFurtherFileSubContainer1 {
  flex: 1 1 0;
  color: #757575;
  font-size: 16px;
  font-family: Outfit;
  font-weight: 400;
  line-height: 20.8px;
  word-wrap: break-word;
}

.noFurtherFileContainer2 {
  width: -webkit-fill-available;
  padding: 12px 20px;
  background: #feeaea;
  border-radius: 8px;
  overflow: hidden;
  align-items: flex-start;
  gap: 8px;
  display: inline-flex;
  border-left: 6px solid #ec7a7a;
  margin-bottom: 44px;
}

.noFurtherFileSubContainer2 {
  flex: 1 1 0;
  color: #757575;
  font-size: 16px;
  font-family: Outfit;
  font-weight: 400;
  line-height: 20.8px;
  word-wrap: break-word;
}
</style>
